import React, { useReducer, useEffect, useCallback, useRef, useState } from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Alert from 'react-bootstrap/Alert';
import { Helmet } from 'react-helmet';
import emailjs from '@emailjs/browser';
import { format } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { createAppointment } from '../services/appointmentService';
import { createLeadWithAppointment } from '../services/leadService';
import { getErrorMessage } from '../utils/errorHandler';
import data from '../data/paomakeup';

import StepProgress from '../components/scheduling/StepProgress';
import ServiceWithArtistSelector from '../components/scheduling/ServiceWithArtistSelector';
import DateTimeSelector from '../components/scheduling/DateTimeSelector';
import ReviewStep from '../components/scheduling/ReviewStep';
import ConfirmationStep, { SuccessStep } from '../components/scheduling/ConfirmationStep';
import SelectedServicesPopup from '../components/scheduling/SelectedServicesPopup';
import AuthGateStep from '../components/scheduling/AuthGateStep';
import ArtistPickerDialog from '../components/scheduling/ArtistPickerDialog';

const initialState = {
  step: 1,
  selectedServices: [],
  selectedDate: new Date(),
  selectedSlot: '',
  notes: '',
  isSubmitting: false,
  error: '',
  contactInfo: null, // { name, email, phone } for guest users
};

function reducer(state, action) {
  switch (action.type) {
    case 'SET_STEP':
      return { ...state, step: action.step, error: '' };
    case 'SET_SERVICES':
      return { ...state, selectedServices: action.services };
    case 'SET_DATE':
      return { ...state, selectedDate: action.date, selectedSlot: '' };
    case 'SET_SLOT':
      return { ...state, selectedSlot: action.slot };
    case 'SET_NOTES':
      return { ...state, notes: action.notes };
    case 'SET_CONTACT_INFO':
      return { ...state, contactInfo: action.contactInfo };
    case 'SUBMIT_START':
      return { ...state, isSubmitting: true, error: '' };
    case 'SUBMIT_SUCCESS':
      return { ...state, isSubmitting: false, step: action.successStep };
    case 'SUBMIT_ERROR':
      return { ...state, isSubmitting: false, error: action.error };
    default:
      return state;
  }
}

/*
 * Step mapping:
 *   Auth:  1=Services, 2=DateTime, 3=Review, 4=Confirm, 5=Success
 *   Guest: 1=Services, 2=DateTime, 3=Review, 4=Account, 5=Confirm, 6=Success
 */

const AUTH_STEPS = [
  { label: 'Servicios', number: 1 },
  { label: 'Fecha y Hora', number: 2 },
  { label: 'Verificacion', number: 3 },
  { label: 'Confirmacion', number: 4 },
];

const GUEST_STEPS = [
  { label: 'Servicios', number: 1 },
  { label: 'Fecha y Hora', number: 2 },
  { label: 'Verificacion', number: 3 },
  { label: 'Cuenta', number: 4 },
  { label: 'Confirmacion', number: 5 },
];

const ScheduleCallV2 = () => {
  const { user, isAuthenticated } = useAuth();
  const { cartServices, clearCart } = useCart();
  const [state, dispatch] = useReducer(reducer, initialState);
  const artistDialogOpenerRef = useRef(null);
  const cartLoadedRef = useRef(false);

  /* Diálogo de artista disponible fuera del paso 1 (Fecha y Hora, Verificación):
     la colaboradora se puede cambiar hasta antes de enviar la cita. */
  const [artistDialog, setArtistDialog] = useState({ open: false, index: null });
  const openArtistPicker = (index) => setArtistDialog({ open: true, index });
  const handleArtistSelect = (artistData) => {
    if (artistDialog.index === null) return;
    const updated = [...state.selectedServices];
    updated[artistDialog.index] = {
      ...updated[artistDialog.index],
      employeeId: artistData?.employeeId || null,
      employeeName: artistData?.employeeName || null,
    };
    dispatch({ type: 'SET_SERVICES', services: updated });
    // Cambiar de artista cambia la disponibilidad: se limpia la hora elegida
    // y, si ya estaba en Verificación, vuelve a Fecha y Hora a reconfirmarla.
    dispatch({ type: 'SET_SLOT', slot: '' });
    if (state.step >= 3) dispatch({ type: 'SET_STEP', step: 2 });
  };

  useEffect(() => emailjs.init('OR-hFrEcD28bt5k1j'), []);

  // Pre-load services from cart on mount
  useEffect(() => {
    if (!cartLoadedRef.current && cartServices.length > 0) {
      cartLoadedRef.current = true;
      dispatch({
        type: 'SET_SERVICES',
        services: cartServices.map((svc) => ({
          label: svc.label,
          shortName: svc.shortName,
          categoryName: svc.categoryName,
          subCategoryName: svc.subCategoryName,
          serviceId: svc.serviceId,
          price: svc.price,
          employeeId: null,
          employeeName: null,
        })),
      });
      clearCart();
    }
  }, [cartServices, clearCart]);

  // Determine steps and step offsets based on auth
  const steps = isAuthenticated ? AUTH_STEPS : GUEST_STEPS;
  const confirmStep = isAuthenticated ? 4 : 5;
  const successStep = isAuthenticated ? 5 : 6;

  const buildScheduledAt = useCallback(() => {
    // El slot viene de availability ("HH:mm"); el spec pide "YYYY-MM-DD HH:mm:ss"
    // en hora local del negocio (sin conversión a UTC).
    return `${format(state.selectedDate, 'yyyy-MM-dd')} ${state.selectedSlot}:00`;
  }, [state.selectedDate, state.selectedSlot]);

  const handleConfirm = async () => {
    try {
      dispatch({ type: 'SUBMIT_START' });

      const firstService = state.selectedServices[0];
      const serviceNames = state.selectedServices.map(s => s.label).join(', ');
      // Los servicios ya viajan como líneas reales de la cita (services[]),
      // así que las notas son solo lo que escribió la clienta.
      const allNotes = state.notes || undefined;

      // Resolve contact info
      const contactName = isAuthenticated ? (user?.name || '') : (state.contactInfo?.name || '');
      const contactEmail = isAuthenticated ? (user?.email || '') : (state.contactInfo?.email || '');
      const contactPhone = isAuthenticated ? (user?.phone || '') : (state.contactInfo?.phone || '');

      // Las líneas viajan como services[] = {service_id, employee_id|null}:
      // la colaboradora es POR SERVICIO (null = «cualquiera / se asigna en la
      // visita»). El servidor agrupa las líneas en una sola cita secuencial
      // (sequential_lines lo fija él — no se envía).
      const servicesPayload = state.selectedServices
        .filter((svc) => svc.serviceId)
        .map((svc) => ({
          service_id: svc.serviceId,
          employee_id: svc.employeeId || null,
        }));

      if (isAuthenticated) {
        await createAppointment({
          // La 1ª línea también va top-level (back-compat requerido)
          service_id: firstService.serviceId,
          employee_id: firstService.employeeId || null,
          services: servicesPayload,
          scheduled_at: buildScheduledAt(),
          notes: allNotes,
        });
      } else {
        await createLeadWithAppointment({
          name: contactName,
          email: contactEmail,
          phone: contactPhone,
          source: 'appointment_form',
          service_id: firstService.serviceId,
          employee_id: firstService.employeeId || null,
          services: servicesPayload,
          scheduled_at: buildScheduledAt(),
          appointment_notes: allNotes,
        });
      }

      // Send email notification
      emailjs.send('service_yh80bao', 'template_8db5huo', {
        fullName: contactName,
        email: contactEmail,
        phoneNumber: contactPhone,
        desiredService: serviceNames,
        desiredDate: format(state.selectedDate, 'dd/MM/yyyy'),
        desiredTime: state.selectedSlot,
        receiveService: 'Estudio',
        contactVia: 'Whatsapp',
        message: state.notes || '',
      }, 'OR-hFrEcD28bt5k1j').catch(err => console.warn('EmailJS failed:', err));

      dispatch({ type: 'SUBMIT_SUCCESS', successStep });
    } catch (err) {
      dispatch({ type: 'SUBMIT_ERROR', error: getErrorMessage(err) });
    }
  };

  const handleContinueFromStep1 = () => {
    if (state.selectedServices.length > 0) {
      dispatch({ type: 'SET_STEP', step: 2 });
    }
  };

  const handleRemoveService = (index) => {
    dispatch({
      type: 'SET_SERVICES',
      services: state.selectedServices.filter((_, i) => i !== index),
    });
  };

  const handleArtistClickFromPopup = (index) => {
    if (artistDialogOpenerRef.current) {
      artistDialogOpenerRef.current(index);
    }
  };

  // After Review step: if authenticated skip to confirm, else go to account step
  const handleAfterReview = () => {
    if (isAuthenticated) {
      dispatch({ type: 'SET_STEP', step: 4 }); // Confirm step for auth
    } else {
      dispatch({ type: 'SET_STEP', step: 4 }); // Account step for guest
    }
  };

  // AuthGateStep callbacks
  const handleAuthenticated = () => {
    // User just logged in/registered — isAuthenticated becomes true on next render.
    // Auth confirmStep is always 4, so stay on step 4 (AuthGateStep hides, ConfirmationStep shows).
    dispatch({ type: 'SET_STEP', step: 4 });
  };

  const handleContinueWithContact = (contactInfo) => {
    dispatch({ type: 'SET_CONTACT_INFO', contactInfo });
    dispatch({ type: 'SET_STEP', step: 5 }); // confirm step in guest flow
  };

  // Success screen
  if (state.step === successStep) {
    return (
      <Container className="py-4">
        <Helmet>
          <title>Cita Confirmada - {data.title}</title>
        </Helmet>
        <Row className="justify-content-center">
          <Col xs={12} sm={10}>
            <SuccessStep isAuthenticated={isAuthenticated} />
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container className="py-4" style={{ paddingBottom: state.step === 1 && state.selectedServices.length > 0 ? 70 : undefined }}>
      <Helmet>
        <title>Reserva tu cita - {data.title}</title>
        <meta name="description" content={data.description} />
      </Helmet>

      <Row className="justify-content-center">
        <Col xs={12} sm={10}>
          {/* Header */}
          <div className="text-center mb-4">
            <h1 className="h4 fw-bold" style={{ color: '#333' }}>Reserva tu cita</h1>
            <p className="small" style={{ color: '#C48BA0' }}>Toma menos de 1 minuto</p>
          </div>

          <StepProgress currentStep={state.step} steps={steps} />

          {state.error && (
            <Alert variant="danger" className="small">{state.error}</Alert>
          )}

          {/* Step 1: Services */}
          {state.step === 1 && (
            <div>
              <ServiceWithArtistSelector
                selectedServices={state.selectedServices}
                onServicesChange={(services) => dispatch({ type: 'SET_SERVICES', services })}
                onArtistDialogOpen={(opener) => { artistDialogOpenerRef.current = opener; }}
              />
            </div>
          )}

          {/* Step 2: Date & Time */}
          {state.step === 2 && (
            <DateTimeSelector
              services={state.selectedServices}
              selectedDate={state.selectedDate}
              selectedSlot={state.selectedSlot}
              notes={state.notes}
              onDateChange={(date) => dispatch({ type: 'SET_DATE', date })}
              onSlotChange={(slot) => dispatch({ type: 'SET_SLOT', slot })}
              onNotesChange={(notes) => dispatch({ type: 'SET_NOTES', notes })}
              onBack={() => dispatch({ type: 'SET_STEP', step: 1 })}
              onContinue={() => dispatch({ type: 'SET_STEP', step: 3 })}
              onPickArtist={openArtistPicker}
            />
          )}

          {/* Step 3: Review */}
          {state.step === 3 && (
            <ReviewStep
              services={state.selectedServices}
              selectedDate={state.selectedDate}
              selectedSlot={state.selectedSlot}
              notes={state.notes}
              onEdit={(step) => dispatch({ type: 'SET_STEP', step })}
              onContinue={handleAfterReview}
              onPickArtist={openArtistPicker}
            />
          )}

          {/* Step 4 (Guest only): Account / Auth Gate */}
          {state.step === 4 && !isAuthenticated && (
            <AuthGateStep
              onAuthenticated={handleAuthenticated}
              onContinueWithContact={handleContinueWithContact}
              onBack={() => dispatch({ type: 'SET_STEP', step: 3 })}
            />
          )}

          {/* Confirmation step (Step 4 for auth, Step 5 for guest) */}
          {state.step === confirmStep && (
            <ConfirmationStep
              isSubmitting={state.isSubmitting}
              onBack={() => dispatch({ type: 'SET_STEP', step: isAuthenticated ? 3 : 4 })}
              onConfirm={handleConfirm}
            />
          )}
        </Col>
      </Row>

      {/* Floating popup for Step 1 */}
      {state.step === 1 && (
        <SelectedServicesPopup
          selectedServices={state.selectedServices}
          onRemoveService={handleRemoveService}
          onArtistClick={handleArtistClickFromPopup}
          onContinue={handleContinueFromStep1}
        />
      )}

      {/* Diálogo de artista para los pasos 2 y 3 */}
      {artistDialog.index !== null && (
        <ArtistPickerDialog
          open={artistDialog.open}
          onOpenChange={(open) => setArtistDialog(prev => ({ ...prev, open }))}
          serviceId={state.selectedServices[artistDialog.index]?.serviceId}
          serviceName={state.selectedServices[artistDialog.index]?.shortName || state.selectedServices[artistDialog.index]?.label}
          onSelect={handleArtistSelect}
        />
      )}
    </Container>
  );
};

export default ScheduleCallV2;
